import type { Block as ArticleBlock } from "@tellescope/types-models";
import { sanitize_html_with_links } from "@tellescope/utilities";
import { toYouTubeEmbedUrl } from "@tellescope/utils";
import { useTranslation } from "react-i18next";

interface ArticleBlockRendererProps {
  blocks: ArticleBlock[];
}

interface SingleBlockProps {
  block: ArticleBlock;
}

/**
 * Renders a single article content block based on its type.
 */
function ArticleContentBlock({ block }: SingleBlockProps) {
  const { t } = useTranslation();
  const baseStyle = block.style
    ? {
        backgroundColor: block.style.backgroundColor,
        borderColor: block.style.borderColor,
        borderWidth: block.style.borderWidth ? `${block.style.borderWidth}px` : undefined,
        borderStyle: block.style.borderWidth ? "solid" : undefined,
        color: block.style.textColor,
      }
    : undefined;

  switch (block.type) {
    case "h1":
      return (
        <h1 className="text-3xl font-bold tracking-tight" style={baseStyle}>
          {block.info.text}
        </h1>
      );

    case "h2":
      return (
        <h2 className="text-2xl font-semibold tracking-tight" style={baseStyle}>
          {block.info.text}
        </h2>
      );

    case "html":
      return (
        <div
          className="prose prose-sm max-w-none dark:prose-invert"
          style={baseStyle}
          dangerouslySetInnerHTML={{ __html: sanitize_html_with_links(block.info.html) }}
        />
      );

    case "raw-html":
      return (
        <div
          className="prose prose-sm max-w-none dark:prose-invert"
          style={baseStyle}
          dangerouslySetInnerHTML={{ __html: sanitize_html_with_links(block.info.html) }}
        />
      );

    case "image": {
      const imgStyle: React.CSSProperties = {
        ...baseStyle,
        maxWidth: block.info.maxWidth ? `${block.info.maxWidth}px` : "100%",
        maxHeight: block.info.maxHeight ? `${block.info.maxHeight}px` : undefined,
        width: block.info.width ? `${block.info.width}px` : "100%",
        height: block.info.height ? `${block.info.height}px` : "auto",
      };
      return (
        <img
          src={block.info.link}
          alt={block.info.alt || block.info.name || "Article image"}
          className="rounded-lg object-contain"
          style={imgStyle}
        />
      );
    }

    case "youtube": {
      const videoUrl = toYouTubeEmbedUrl(block.info.link);
      if (!videoUrl) return null;
      const iframeStyle: React.CSSProperties = {
        ...baseStyle,
        maxWidth: block.info.maxWidth ? `${block.info.maxWidth}px` : "100%",
        maxHeight: block.info.maxHeight ? `${block.info.maxHeight}px` : undefined,
        width: block.info.width ? `${block.info.width}px` : "100%",
        height: block.info.height ? `${block.info.height}px` : undefined,
      };
      return (
        <div className="aspect-video w-full" style={iframeStyle}>
          <iframe
            src={videoUrl}
            title={block.info.name || "YouTube video"}
            className="w-full h-full rounded-lg"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }

    case "pdf": {
      // Use PDF preview service for consistent rendering
      const pdfPreviewUrl = encodeURI(`https://pdf.tellescope.com?url=${block.info.link}`);
      const iframeStyle: React.CSSProperties = {
        ...baseStyle,
        maxWidth: block.info.maxWidth ? `${block.info.maxWidth}px` : "100%",
        minHeight: "500px",
      };
      return (
        <iframe
          src={pdfPreviewUrl}
          title={block.info.name || "PDF document"}
          className="w-full h-[70vh] rounded-lg border"
          style={iframeStyle}
        />
      );
    }

    case "iframe": {
      const iframeStyle: React.CSSProperties = {
        ...baseStyle,
        maxWidth: block.info.maxWidth ? `${block.info.maxWidth}px` : "100%",
        maxHeight: block.info.maxHeight ? `${block.info.maxHeight}px` : undefined,
        width: block.info.width ? `${block.info.width}px` : "100%",
        height: block.info.height ? `${block.info.height}px` : "400px",
      };
      return (
        <iframe
          src={block.info.link}
          title={block.info.name || t("Embedded content")}
          className="w-full rounded-lg border"
          style={iframeStyle}
        />
      );
    }

    case "content-link":
      // Content links reference other ManagedContentRecords
      // For now, just show a placeholder - could be enhanced to load and display the linked content
      return (
        <div className="p-4 border rounded-lg bg-muted/50 text-muted-foreground text-sm">
          Linked content: {block.info.recordId}
        </div>
      );

    default: {
      // Handle unknown block types gracefully
      const _exhaustive: never = block;
      return null;
    }
  }
}

/**
 * Renders an array of article content blocks as a vertical column.
 * Used for displaying ManagedContentRecord articles that have a blocks array.
 */
export function ArticleBlockRenderer({ blocks }: ArticleBlockRendererProps) {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {blocks.map((block, index) => (
        <ArticleContentBlock key={index} block={block} />
      ))}
    </div>
  );
}
